import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface AnnouncerMessage {
    message: string;
    polite: 'polite' | 'assertive' | 'off';
}

// Context for actions/setters only
interface AnnouncerContextType {
    announce: (message: string, polite?: 'polite' | 'assertive' | 'off') => void;
    clearAnnouncement: () => void;
}

const AnnouncerContext = createContext<AnnouncerContextType | undefined>(undefined);

interface AnnouncerProviderProps {
    children: ReactNode;
}

export const AnnouncerProvider: React.FC<AnnouncerProviderProps> = ({ children }) => {
    const [currentMessage, setCurrentMessage] = useState<AnnouncerMessage | null>(null);

    const announce = useCallback((message: string, polite: 'polite' | 'assertive' | 'off' = 'polite') => {
        setCurrentMessage({ message, polite });
    }, []);

    const clearAnnouncement = useCallback(() => {
        setCurrentMessage(null);
    }, []);

    const contextValue = { announce, clearAnnouncement };

    return (
        <AnnouncerContext.Provider value={contextValue}>
            {children}
            {/* Render the live region */}
            <AnnouncerLiveRegion message={currentMessage?.message} polite={currentMessage?.polite} />
        </AnnouncerContext.Provider>
    );
};

interface AnnouncerLiveRegionProps {
    message?: string;
    polite?: 'polite' | 'assertive' | 'off';
}

const AnnouncerLiveRegion: React.FC<AnnouncerLiveRegionProps> = ({ message, polite = 'polite' }) => {
    if (!message) return null;

    return (
        <div
            aria-live={polite}
            aria-atomic='true'
            style={{
                position: 'absolute',
                left: '-10000px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                opacity: 0,
            }}
            role='status'
            aria-label='Live announcement'
        >
            <span>{message}</span>
        </div>
    );
};

export const useAnnouncer = (): AnnouncerContextType => {
    const context = useContext(AnnouncerContext);
    if (context === undefined) {
        throw new Error('useAnnouncer must be used within an AnnouncerProvider');
    }
    return context;
};
